import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import {
  AlertTriangle,
  Ban,
  Briefcase,
  Clock,
  KeyRound,
  Pencil,
  ShieldCheck,
  Trash2,
  UserPlus,
  UserRound,
  Users,
} from "lucide-react";
import { formatDate } from "@/utils/formatters";
import PageHeader from "@/components/shared/PageHeader";
import BulkActions from "@/components/shared/BulkActions";
import DataExportButton from "@/components/shared/DataExportButton";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { Field, PasswordInput } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OpsPersonCell } from "@/components/admin/ops-table";
import { useAuthStore } from "@/store/authStore";
import {
  useAdminUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useDeactivateUser,
  useResetUserPassword,
} from "@/hooks/useAdminData";
import { demoUsers } from "@/data/demoData";
import { getFileUrl } from "@/lib/api-client";

export const Route = createFileRoute("/admin/users/")({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selected, setSelected] = useState<string[]>([]);
  const { user: currentUser } = useAuthStore();
  const { data: usersData = [], isLoading } = useAdminUsers();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("student");
  const [editStatus, setEditStatus] = useState("active");

  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState("student");

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const deactivateUser = useDeactivateUser();
  const resetPassword = useResetUserPassword();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetResult, setResetResult] = useState<{ email: string; temporary_password: string } | null>(null);

  const displayUsers = currentUser?.isDemo ? demoUsers : usersData;

  const filteredUsers = displayUsers.filter((u: any) => {
    const name = u.name || "";
    const email = u.email || "";
    const matchesSearch =
      name.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role?.toLowerCase() === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleSelect = (id: string) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const openEditDialog = (u: any) => {
    setSelectedUser(u);
    setEditName(u.name || "");
    setEditEmail(u.email || "");
    setEditRole(u.role?.toLowerCase() || "student");
    setEditStatus(u.status || "active");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    updateUser.mutate(
      {
        id: selectedUser.id,
        payload: { name: editName, email: editEmail, role: editRole, status: editStatus },
      },
      { onSettled: () => setEditDialogOpen(false) },
    );
  };

  const handleDeactivate = () => {
    deactivateUser.mutate(selectedUser.id, { onSettled: () => setDeactivateDialogOpen(false) });
  };

  const handleDelete = () => {
    deleteUser.mutate(selectedUser.id, { onSettled: () => setDeleteDialogOpen(false) });
  };

  const handleCreate = () => {
    createUser.mutate(
      { name: createName, email: createEmail, password: createPassword, role: createRole },
      {
        onSettled: () => {
          setCreateDialogOpen(false);
          setCreateName("");
          setCreateEmail("");
          setCreatePassword("");
          setCreateRole("student");
        },
      },
    );
  };


  const allSelected = selected.length === filteredUsers.length && filteredUsers.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="User management"
        subtitle="Comprehensive control over system users, roles, and platform permissions."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Users" }]}
        action={
          <>
            <DataExportButton
              onExport={(format) => console.log("Exporting users as", format)}
              label="Export data"
            />
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <UserPlus />
              Create user
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
        <StatCard label="Total platform users" value={displayUsers.length} icon={<Users />} />
        <StatCard
          label="Active students"
          value={displayUsers.filter((u: any) => u.role === "Student").length}
          icon={<UserRound />}
        />
        <StatCard
          label="Partner accounts"
          value={displayUsers.filter((u: any) => u.role === "Partner").length}
          icon={<Briefcase />}
        />
        <StatCard
          label="Pending approvals"
          value={displayUsers.filter((u: any) => u.status === "pending").length}
          icon={<Clock />}
        />
      </div>

      {/* Directory */}
      <SectionCard
        title="User directory"
        description={`${filteredUsers.length} of ${displayUsers.length} users`}
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search users..."
              aria-label="Search users"
            />
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="h-8 w-full sm:w-[130px]" aria-label="Filter by role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="student">Students</SelectItem>
                <SelectItem value="partner">Partners</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : (
          <>
            {selected.length > 0 && (
              <div className="border-b border-border px-4 py-2">
                <BulkActions
                  className="mb-0"
                  selectedCount={selected.length}
                  onClear={() => setSelected([])}
                  showApprove={false}
                  showReject={false}
                />
              </div>
            )}

            {filteredUsers.length === 0 ? (
              <EmptyState
                bare
                icon={<Users />}
                title="No users found"
                description="Nothing matches this search or role."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10 pl-4">
                      <Checkbox
                        aria-label="Select all users"
                        checked={allSelected}
                        onCheckedChange={() => {
                          if (selected.length === filteredUsers.length) setSelected([]);
                          else setSelected(filteredUsers.map((u: any) => u.id));
                        }}
                      />
                    </TableHead>
                    <TableHead>User details</TableHead>
                    <TableHead>Access level</TableHead>
                    <TableHead>Account status</TableHead>
                    <TableHead>Registration date</TableHead>
                    <TableHead className="pr-4 text-right">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u: any) => {
                    const isSelected = selected.includes(u.id);
                    return (
                      <TableRow key={u.id} data-state={isSelected ? "selected" : undefined}>
                        <TableCell className="w-10 pl-4">
                          <Checkbox
                            aria-label={`Select ${u.name || u.email || "user"}`}
                            checked={isSelected}
                            onCheckedChange={() => handleSelect(u.id)}
                          />
                        </TableCell>
                        <TableCell className="min-w-56 py-1.5">
                          <OpsPersonCell
                            name={u.name || "Unnamed User"}
                            initial={u.name?.charAt(0) || "?"}
                            secondary={u.email}
                            avatarUrl={u.avatar_url ? getFileUrl(u.avatar_url) : undefined}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={u.role === "Student" ? "lavender" : u.role === "Admin" ? "default" : "info"}
                          >
                            {u.role === "Admin" && <ShieldCheck aria-hidden />}
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={u.status} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                          {formatDate(u.joined || u.created_at) || "N/A"}
                        </TableCell>
                        <TableCell className="py-1.5 pr-3">
                          <div className="flex justify-end gap-0.5">
                            <IconAction
                              label="Edit credentials"
                              icon={<Pencil />}
                              onClick={() => openEditDialog(u)}
                            />
                            <IconAction
                              label="Reset password"
                              icon={<KeyRound />}
                              disabled={resetPassword.isPending}
                              onClick={async () => {
                                const userId = u.id;
                                if (!userId) return;
                                const result = await resetPassword.mutateAsync(userId);
                                setResetResult(result);
                                setResetDialogOpen(true);
                              }}
                            />
                            <IconAction
                              label="Deactivate account"
                              tone="destructive"
                              icon={<Ban />}
                              onClick={() => {
                                setSelectedUser(u);
                                setDeactivateDialogOpen(true);
                              }}
                            />
                            <IconAction
                              label="Remove permanently"
                              tone="destructive"
                              icon={<Trash2 />}
                              onClick={() => {
                                setSelectedUser(u);
                                setDeleteDialogOpen(true);
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </SectionCard>

      {/* Create user dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(o) => !o && setCreateDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create user</DialogTitle>
            <DialogDescription>Add an account and choose its access level.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Full name">
              <Input value={createName} onChange={(e) => setCreateName(e.target.value)} />
            </Field>
            <Field label="Email address">
              <Input
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
              />
            </Field>
            <Field label="Password">
              <PasswordInput
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Role">
              {({ id }) => (
                <Select value={createRole} onValueChange={setCreateRole}>
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit user credentials</DialogTitle>
            <DialogDescription>Update profile, role and account status.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name" className="sm:col-span-2">
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </Field>
            <Field label="Email address" className="sm:col-span-2">
              <Input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
              />
            </Field>
            <Field label="Role">
              {({ id }) => (
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Account status">
              {({ id }) => (
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate user dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onOpenChange={(o) => !o && setDeactivateDialogOpen(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate account?</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate{" "}
              <span className="font-medium text-foreground">{selectedUser?.name}</span>? They will
              lose access to the portal immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeactivate}>
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive max-sm:mx-auto">
              <AlertTriangle className="size-4" aria-hidden />
            </div>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete{" "}
              <span className="font-medium text-foreground">{selectedUser?.name}</span>'s account?
              This action cannot be undone and will remove all their associated data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <AlertTriangle />
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password result dialog */}
      <Dialog
        open={resetDialogOpen}
        onOpenChange={(o) => {
          if (!o) {
            setResetDialogOpen(false);
            setResetResult(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-warning/30 bg-warning/10 text-warning max-sm:mx-auto">
              <KeyRound className="size-4" aria-hidden />
            </div>
            <DialogTitle>Password reset</DialogTitle>
            <DialogDescription>
              A temporary password has been sent to{" "}
              <span className="font-medium text-foreground">{resetResult?.email}</span>.
            </DialogDescription>
          </DialogHeader>
          {resetResult?.temporary_password && (
            <div className="rounded-md border border-border bg-card p-3">
              <p className="eyebrow mb-1">Temporary password</p>
              <p className="font-mono text-base tracking-wider text-foreground select-all">
                {resetResult.temporary_password}
              </p>
            </div>
          )}
          <p className="text-caption text-muted-foreground">
            The user will be notified and must change their password on next login.
          </p>
          <DialogFooter>
            <Button
              onClick={() => {
                setResetDialogOpen(false);
                setResetResult(null);
              }}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
